/**
 * Triangle de Pascal — calculs purs.
 *
 * Ligne n, case k : « k parmi n ». Chaque case est la somme des deux du
 * dessus (relation de Pascal), et compte les chemins qui descendent du
 * sommet avec k pas à droite parmi n.
 */

export const ROWS = 10; // lignes 0 à 10

export function binom(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  k = Math.min(k, n - k);
  let r = 1;
  for (let i = 1; i <= k; i++) r = (r * (n - k + i)) / i;
  return Math.round(r);
}

export function triangle(rows = ROWS): number[][] {
  return Array.from({ length: rows + 1 }, (_, n) =>
    Array.from({ length: n + 1 }, (_, k) => binom(n, k)),
  );
}

/** Les deux cases du dessus, ou null sur les bords (k = 0 ou k = n). */
export function parents(n: number, k: number): [[number, number], [number, number]] | null {
  if (n === 0 || k === 0 || k === n) return null;
  return [
    [n - 1, k - 1],
    [n - 1, k],
  ];
}

export type Step = 'G' | 'D';

/** Un chemin du sommet à (n, k) : n pas, dont k vers la droite, au hasard. */
export function randomPath(n: number, k: number, rng: () => number): Step[] {
  const steps: Step[] = [];
  let right = k;
  for (let i = n; i > 0; i--) {
    // probabilité d'aller à droite = cases restantes à droite / pas restants
    const goRight = right > 0 && (right === i || rng() < right / i);
    steps.push(goRight ? 'D' : 'G');
    if (goRight) right--;
  }
  return steps;
}

/** Cases visitées par un chemin, du sommet (0, 0) à l'arrivée. */
export function pathCells(steps: readonly Step[]): Array<[number, number]> {
  const cells: Array<[number, number]> = [[0, 0]];
  let k = 0;
  steps.forEach((s, i) => {
    if (s === 'D') k++;
    cells.push([i + 1, k]);
  });
  return cells;
}

/** Défi : une valeur à trouver dans le triangle (hors bords à 1). */
export function pickTarget(rng: () => number, previous?: number): number {
  const values = [
    ...new Set(
      triangle()
        .flatMap((row) => row)
        .filter((v) => v > 2),
    ),
  ].sort((a, b) => a - b);
  for (let i = 0; i < 20; i++) {
    const v = values[Math.min(values.length - 1, Math.floor(rng() * values.length))]!;
    if (v !== previous) return v;
  }
  return values.find((v) => v !== previous)!;
}

export const CHALLENGE_ROUNDS = 5;
